import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, BACKEND_URL, openApiFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, FileText, RefreshCw, ShieldCheck } from "lucide-react";
import { ClienteCombobox } from "@/components/ClienteCombobox";
import { ProductoCombobox } from "@/components/ProductoCombobox";

const UNIDADES = ["CÁPSULAS", "TABLETAS", "FRASCOS", "AMPOLLAS", "SOBRES", "UNIDADES", "CAJAS", "VIALES", "JERINGAS"];

function emptyForm() {
  return {
    tipo: "canje",
    cliente_id: "",
    fecha: new Date().toISOString().slice(0, 10),
    producto_comercial_id: "",
    producto_nombre: "",
    registro_sanitario: "",
    registro_sanitario_caduca: "",
    cantidad: 0,
    unidad_texto: "CÁPSULAS",
    pedido_compra_nro: "",
    es_parcial: false,
    lotes: [],
    validez_garantia: "Un año",
  };
}

export default function Letters() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [prods, setProds] = useState([]);
  const [tabTipo, setTabTipo] = useState("canje");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [c, p, ca] = await Promise.all([
      api.get("/masters/clientes"),
      api.get("/masters/productos-comerciales"),
      api.get("/letters/cartas"),
    ]);
    setClientes(c.data);
    setProds(p.data);
    setItems(ca.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const onProdChange = (val) => {
    const p = prods.find(x => x.id === val);
    setForm(f => ({
      ...f,
      producto_comercial_id: val,
      producto_nombre: p?.nombre_comercial || f.producto_nombre,
      registro_sanitario: p?.registro_sanitario || f.registro_sanitario,
    }));
  };

  const sugerirLotes = async () => {
    if (!form.producto_comercial_id) return toast.error("Seleccione un producto comercial");
    try {
      const { data } = await api.get(`/letters/sugerir-lotes/${form.producto_comercial_id}?cantidad=${form.cantidad || 0}`);
      const lotes = data.filter(x => x.sugerido > 0).map(x => ({ numero: x.numero, caduca: x.caduca, cantidad: x.sugerido }));
      if (lotes.length === 0) return toast.info("No hay lotes disponibles para esa cantidad. Puede ingresarlos manualmente.");
      setForm(f => ({ ...f, lotes, es_parcial: lotes.length > 1 || (lotes[0]?.cantidad < f.cantidad) }));
      toast.success(`${lotes.length} lote(s) sugeridos por FEFO`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addLote = () => setForm(f => ({ ...f, lotes: [...f.lotes, { numero: "", caduca: "", cantidad: 0 }] }));
  const updLote = (i, field, val) => setForm(f => {
    const nl = [...f.lotes]; nl[i] = { ...nl[i], [field]: val }; return { ...f, lotes: nl };
  });
  const delLote = (i) => setForm(f => ({ ...f, lotes: f.lotes.filter((_, x) => x !== i) }));

  const save = async () => {
    if (!form.cliente_id) return toast.error("Seleccione cliente");
    if (!form.producto_nombre) return toast.error("Complete nombre del producto");
    try {
      await api.post("/letters/cartas", form);
      toast.success(`Carta de ${form.tipo === "canje" ? "canje" : "garantía técnica"} generada`);
      setDialogOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const verPDF = (it) => {
    openApiFile(`/letters/cartas/${it.id}/pdf`, { filename: `CARTA-${it.numero || it.id.slice(0,8)}.pdf` })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const eliminar = async (it) => {
    if (!window.confirm(`¿Eliminar ${it.numero}?`)) return;
    try { await api.delete(`/letters/cartas/${it.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtrados = useMemo(() => items.filter(x => x.tipo === tabTipo), [items, tabTipo]);
  const totalCantidadLotes = useMemo(() => form.lotes.reduce((s, l) => s + (parseFloat(l.cantidad) || 0), 0), [form.lotes]);

  return (
    <div className="space-y-6" data-testid="letters-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Documentos de ventas</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Cartas de Canje y Garantía</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Compromisos de canje y garantías técnicas por producto. Soporta entregas parciales con lotes específicos.</p>
        </div>
        <Button onClick={() => { setForm({ ...emptyForm(), tipo: tabTipo }); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="new-letter-btn">
          <Plus size={16} className="mr-2" />Nueva carta
        </Button>
      </div>

      <Tabs value={tabTipo} onValueChange={setTabTipo}>
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="canje" data-testid="tab-canje"><RefreshCw size={14} className="mr-2" />Compromiso de canje</TabsTrigger>
          <TabsTrigger value="garantia" data-testid="tab-garantia"><ShieldCheck size={14} className="mr-2" />Garantía técnica</TabsTrigger>
        </TabsList>
        <TabsContent value={tabTipo}>
          <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Número</th><th>Fecha</th><th>Cliente</th><th>Producto</th>
                  <th className="text-right">Cantidad</th><th>Pedido compra</th><th>Lotes</th><th className="w-32"></th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(it => (
                  <tr key={it.id}>
                    <td className="font-mono text-xs font-semibold">{it.numero}</td>
                    <td>{it.fecha}</td>
                    <td className="max-w-[200px] truncate" title={it.cliente_snapshot?.razon_social}>{it.cliente_snapshot?.razon_social || "—"}</td>
                    <td className="max-w-[240px] truncate" title={it.producto_nombre}>{it.producto_nombre}</td>
                    <td className="text-right tabular-nums font-semibold">{it.cantidad} <span className="text-[#8ba397] text-xs">{it.unidad_texto}</span></td>
                    <td className="font-mono text-xs">{it.pedido_compra_nro || "—"}</td>
                    <td>
                      {it.lotes?.length > 0 ? (
                        <div className="flex gap-1 flex-wrap">
                          {it.lotes.map((l, i) => <Badge key={i} className="bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]" variant="outline">{l.numero}</Badge>)}
                          {it.es_parcial && <Badge variant="outline" className="bg-[#fef7ec] text-[#8a5500] border-[#f5c477]">Parcial</Badge>}
                        </div>
                      ) : <span className="text-[#8ba397] text-xs">—</span>}
                    </td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => verPDF(it)} data-testid={`pdf-${it.id}`}><FileText size={14} className="mr-1" />PDF</Button>
                        <Button size="icon" variant="ghost" onClick={() => eliminar(it)}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtrados.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8ba397]">Sin cartas emitidas aún</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva carta de {form.tipo === "canje" ? "compromiso de canje" : "garantía técnica"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="letter-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="canje">Compromiso de canje</SelectItem>
                  <SelectItem value="garantia">Garantía técnica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Cliente</Label>
              <ClienteCombobox
                value={form.cliente_id}
                onChange={(v) => setForm({ ...form, cliente_id: v })}
                clientes={clientes}
                placeholder="Buscar por razón social, RUC o ciudad…"
                testId="letter-client"
              />
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Producto comercial (opcional)</Label>
              <ProductoCombobox
                value={form.producto_comercial_id}
                onChange={onProdChange}
                productos={prods}
                placeholder="Elegir para autocompletar..."
                testId="letter-product"
              />
            </div>
            <div className="col-span-3"><Label>Detalle del producto (nombre completo)</Label><Input value={form.producto_nombre} onChange={(e) => setForm({ ...form, producto_nombre: e.target.value })} placeholder="ITRACONAZOL 100 MG. SÓLIDO ORAL" /></div>
            <div className="col-span-1">
              <Label>Cantidad</Label>
              <Input type="number" value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Unidad</Label>
              <Select value={form.unidad_texto} onValueChange={(v) => setForm({ ...form, unidad_texto: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{UNIDADES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Pedido de compra (cliente)</Label><Input value={form.pedido_compra_nro} onChange={(e) => setForm({ ...form, pedido_compra_nro: e.target.value })} placeholder="JBG0000108736" /></div>
            <div className="col-span-2"><Label>Registro sanitario</Label><Input value={form.registro_sanitario} onChange={(e) => setForm({ ...form, registro_sanitario: e.target.value })} placeholder="4375-MEE-0918" /></div>
            <div><Label>Caducidad R.S.</Label><Input value={form.registro_sanitario_caduca} onChange={(e) => setForm({ ...form, registro_sanitario_caduca: e.target.value })} placeholder="03-09-2028" /></div>
            {form.tipo === "garantia" && (
              <div className="col-span-3"><Label>Validez garantía</Label><Input value={form.validez_garantia} onChange={(e) => setForm({ ...form, validez_garantia: e.target.value })} /></div>
            )}
          </div>

          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Lotes entregados</div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-xs text-[#4a5c53]">
                  <Checkbox checked={form.es_parcial} onCheckedChange={(v) => setForm({ ...form, es_parcial: !!v })} data-testid="parcial-checkbox" />
                  Entrega parcial
                </label>
                <Button size="sm" variant="outline" onClick={sugerirLotes} disabled={!form.producto_comercial_id} data-testid="suggest-fefo-btn">
                  <RefreshCw size={12} className="mr-1" />Sugerir FEFO
                </Button>
                <Button size="sm" variant="outline" onClick={addLote}><Plus size={12} className="mr-1" />Agregar</Button>
              </div>
            </div>
            <table className="erp-table">
              <thead><tr><th>N° Lote</th><th>Caduca</th><th className="w-28 text-right">Cantidad</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.lotes.map((l, i) => (
                  <tr key={i}>
                    <td><Input className="h-9" value={l.numero} onChange={(e) => updLote(i, "numero", e.target.value)} placeholder="2100885" /></td>
                    <td><Input className="h-9" value={l.caduca} onChange={(e) => updLote(i, "caduca", e.target.value)} placeholder="10-2027" /></td>
                    <td><Input className="h-9 text-right" type="number" value={l.cantidad || 0} onChange={(e) => updLote(i, "cantidad", parseFloat(e.target.value) || 0)} /></td>
                    <td><Button size="icon" variant="ghost" onClick={() => delLote(i)}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
                {form.lotes.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-xs text-[#8ba397]">Agregue lotes o use "Sugerir FEFO"</td></tr>}
              </tbody>
              {form.lotes.length > 0 && (
                <tfoot className="bg-[#f7f9f8] border-t">
                  <tr><td colSpan={2} className="text-right uppercase text-xs font-semibold text-[#5c7368] py-2 px-3">Suma lotes</td><td className="text-right font-semibold tabular-nums py-2 px-3">{totalCantidadLotes} {form.unidad_texto}</td><td></td></tr>
                </tfoot>
              )}
            </table>
          </div>
          {form.lotes.length > 0 && totalCantidadLotes !== form.cantidad && (
            <div className="text-xs text-[#8a5500] mt-2 bg-[#fef7ec] border border-[#f5c477] rounded px-3 py-1.5">
              ⚠ Suma de lotes ({totalCantidadLotes}) ≠ cantidad total ({form.cantidad}). Marque <strong>Entrega parcial</strong> si aplica.
            </div>
          )}

          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-letter-btn">Generar carta</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
